import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playBeep, startTitleBlink, stopTitleBlink } from "@/lib/notify";
import { toast } from "sonner";
import { MessageSquare, X, Send, ImagePlus, Smile } from "lucide-react";

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const [stickers, setStickers] = useState([]);
  const [showStk, setShowStk] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const prevCount = useRef(0);

  useEffect(() => { api.get("/chat/stickers").then((r) => setStickers(r.data?.stickers || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!user) return;
    const poll = async () => {
      try {
        if (open) {
          const r = await api.get("/chat/messages");
          setMsgs(r.data); setUnread(0); stopTitleBlink();
        } else {
          const r = await api.get("/chat/unread");
          if (r.data.unread > prevCount.current) {
            playBeep();
            startTitleBlink("💬 Balasan baru dari Admin!");
            toast.info("Ada balasan baru dari ROOFTOP PRINT");
          }
          prevCount.current = r.data.unread; setUnread(r.data.unread);
        }
      } catch (e) {}
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, [user, open]);

  useEffect(() => { if (open) { stopTitleBlink(); endRef.current?.scrollIntoView({ behavior: "smooth" }); } }, [msgs, open]);

  if (!user || user.role === "admin") return null;

  const send = async (body) => {
    const r = await api.post("/chat/messages", body);
    setMsgs((m) => [...m, r.data]);
  };
  const sendText = async () => { if (!text.trim()) return; const t = text; setText(""); await send({ text: t }); };
  const sendImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await send({ image_file_id: up.data.file_id });
    if (fileRef.current) fileRef.current.value = "";
  };
  const sendSticker = async (s) => { setShowStk(false); await send({ sticker: s }); };
  const imgUrl = (id) => `${api.defaults.baseURL}/files/${id}`;

  return (
    <div className="fixed bottom-5 right-5 z-[60] no-print">
      {open && (
        <div className="mb-3 w-[340px] max-w-[calc(100vw-2.5rem)] h-[460px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="chat-window">
          <div className="bg-[#00133F] text-white px-4 py-3 flex items-center justify-between">
            <span className="font-heading font-semibold text-sm">Chat dengan ROOFTOP PRINT</span>
            <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
            {msgs.length === 0 && <p className="text-center text-xs text-slate-400 mt-6">Halo! Ada yang bisa kami bantu?</p>}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.sender === "customer" ? "bg-[#F0005A] text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
                  {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                  {m.image_file_id && <a href={imgUrl(m.image_file_id)} target="_blank" rel="noreferrer"><img src={imgUrl(m.image_file_id)} alt="" className="rounded-lg max-w-full mt-1" /></a>}
                  {m.sticker && <img src={m.sticker} alt="stiker" className="w-20 h-20 object-contain" />}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          {showStk && stickers.length > 0 && (
            <div className="border-t border-slate-100 p-2 grid grid-cols-4 gap-2 max-h-28 overflow-y-auto">
              {stickers.map((s, i) => <button key={i} onClick={() => sendSticker(s)}><img src={s} alt="" className="w-14 h-14 object-contain hover:scale-110 transition-transform" /></button>)}
            </div>
          )}
          <div className="border-t border-slate-100 p-2 flex items-center gap-1.5">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={sendImage} data-testid="chat-image-input" />
            <button onClick={() => fileRef.current?.click()} className="p-2 text-slate-500 hover:text-[#F0005A]"><ImagePlus className="w-5 h-5" /></button>
            {stickers.length > 0 && <button onClick={() => setShowStk((v) => !v)} className="p-2 text-slate-500 hover:text-[#F0005A]"><Smile className="w-5 h-5" /></button>}
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendText()}
              placeholder="Tulis pesan..." className="flex-1 text-sm border border-slate-200 rounded-full px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#F0005A]/50" data-testid="chat-text-input" />
            <button onClick={sendText} className="p-2 bg-[#F0005A] text-white rounded-full" data-testid="chat-send-btn"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      <button onClick={() => setOpen((v) => !v)} data-testid="chat-toggle-btn"
        className="relative flex items-center gap-2 h-14 pl-4 pr-5 rounded-full bg-[#00133F] shadow-xl hover:scale-105 transition-transform">
        <MessageSquare className="w-6 h-6 text-white shrink-0" />
        <span className="text-white font-semibold text-sm">Tanya CS</span>
        {unread > 0 && <span data-testid="chat-unread-badge" className="absolute -top-1 -right-1 bg-[#F0005A] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{unread}</span>}
      </button>
    </div>
  );
}
